// 商品検索のキーワードを、送信前に置き換える（表記ゆれ → 登録されている検索語）。あわせて入力されたままの語を
// RegSearchKey に記録する。wuSiteResource2 の各店 SiteCommon.ts の searchReplace() の引き取り先（Issue #53）。
//
// 旧実装は submit で preventDefault → ReplaceSearchKey を**同期 XHR** で引き（サーバーが本番 DB を SELECT）→ location.href で遷移、
// と同時に RegSearchKey を fire-and-forget していた。
// ★ Issue #62 で置換をここで完結させた（hooks/search-word.ts + hooks/type-codes.ts）。ReplaceSearchKey はもう叩かない:
//   ① 全角英数字→半角はその場で   ② 長音・全角ハイフンを含まなければ通信ゼロで遷移
//   ③ 含むときだけ型番リスト（静的 JSON、1 セッション 1 回）を見て、型番に前方一致したときだけ ー/－ → - にする
//   型番リストが取れなくても ① だけ行って入力語のまま遷移する（検索は止めない）。
// RegSearchKey（記録）はこの Issue では消さない（田口さん: 分析に使っていないが、削除の指示があるまで残す）。非同期・keepalive。
//
// ★ 過渡期に旧実装（jQuery の submit ハンドラ）と同居しても二重に飛ばないよう、
//   旧側より後に束ね（shared/after-old-scripts.ts）、旧側が preventDefault 済みなら何もしない。
//
// RegSearchKey のホストは店によって api.workup-system.com と www.workup-system.com に割れている。
// 挙動を変えないため店ごとの表に持つ（あんしんやは現行どおり api）。

import { afterOldScripts } from '../shared/after-old-scripts'
import { convertSearchWord, needsTypeCodes } from './search-word'
import { loadTypeCodes } from './type-codes'

export interface SearchSite {
  /** 検索フォームのセレクタ（querySelectorAll にそのまま渡す。カンマ区切りで複数可。action は店ごとにスキーム有無が違う） */
  form: string
  /** RegSearchKey（検索語の記録）のオリジン */
  api: string
  /** 遷移先の一覧ページ */
  list: string
}

export const SEARCH: Record<number, SearchSite> = {
  // ワークアップ fukuro.in（Issue #60）。★ 他店と違い form[action=...] ではなく、検索ページ（/page/696）の
  // #search-page .product-search と #kensaku-form（2026-09-22 時点の実機には無い。旧実装の綴りをそのまま残す）。
  // ★ form.search_form（ヘッダーの標準検索窓）は旧実装に無かった意図的な追加（2026-09-22 田口さん指示）。
  //   他 4 店は form[action=…/product-list] で既に対象で、fukuro だけ全角検索が置換されず実質 0 件になっていた。
  //   実機の form は search_form / product-search / item_sort_form / productadd* だけで、search_form は各ページ 1 本。API は www 側
  1: {
    form: '#kensaku-form, #search-page .product-search, form.search_form',
    api: 'https://www.workup-system.com',
    list: 'https://www.fukuro.in/product-list',
  },
  // あんしんや（Issue #53）。★ 旧実装のセレクタ（スキーム無し）はページ本文の #product_search（action="//www.anshinya.com/product-list"）
  // には当たるが、ヘッダーの form.search_form（action="https://…"）には当たっていなかった（Issue #62 で発見）。両方を対象にする
  2: {
    form: "form[action='//www.anshinya.com/product-list'], form.search_form",
    api: 'https://api.workup-system.com',
    list: 'https://www.anshinya.com/product-list',
  },
  // OPP本舗（Issue #70 の 4 本目・最後）。旧実装は form[action='https://www.opp-honpo.net/product-list']（ヘッダーの
  // form.search_form がそれ。トップ・カート・商品ページに 1 本）。API は www 側（現行どおり）
  3: {
    form: "form[action='https://www.opp-honpo.net/product-list']",
    api: 'https://www.workup-system.com',
    list: 'https://www.opp-honpo.net/product-list',
  },
  // 台紙販売（Issue #55）。API は www 側（現行どおり）
  4: {
    form: "form[action='https://www.dansai.jp/product-list']",
    api: 'https://www.workup-system.com',
    list: 'https://www.dansai.jp/product-list',
  },
  // パックジャパン（Issue #58）。旧実装は TS 側に無く、おちゃのこ「アクセス解析タグ設定」欄の直書き。API は www 側
  6: {
    form: "form[action='https://www.pack-japan.jp/product-list']",
    api: 'https://www.workup-system.com',
    list: 'https://www.pack-japan.jp/product-list',
  },
}

export function registerUrl(api: string, siteId: number, keyword: string): string {
  return `${api}/api/SearchKey/RegSearchKey?siteInfoId=${siteId}&key=${encodeURIComponent(keyword)}`
}

export function listUrl(list: string, keyword: string): string {
  return `${list}?keyword=${encodeURIComponent(keyword)}`
}

/** 置換後の語。長音も全角ハイフンも無ければ型番リストを読まない（通信ゼロ）。取れなければ入力語（全角英数だけ半角化）のまま */
export async function replacedKeyword(keyword: string, load: () => Promise<string[] | null> = loadTypeCodes): Promise<string> {
  if (!needsTypeCodes(keyword)) return convertSearchWord(keyword, null)
  return convertSearchWord(keyword, await load())
}

export function bindSearchReplace(form: HTMLFormElement, site: SearchSite, siteId: number): void {
  form.addEventListener('submit', (e) => {
    if (e.defaultPrevented) return // 旧実装（wuSiteResource2）が先に処理した
    e.preventDefault()
    const keyword = form.querySelector<HTMLInputElement>('input[name=keyword]')?.value ?? ''
    // 記録は遷移で打ち切られないよう keepalive
    fetch(registerUrl(site.api, siteId, keyword), { keepalive: true, cache: 'no-store' }).catch(() => {})
    void replacedKeyword(keyword).then((k) => {
      location.href = listUrl(site.list, k)
    })
  })
}

export default function searchReplace(): void {
  const siteId = window.wu?.siteInfoId
  const site = siteId ? SEARCH[siteId] : undefined
  if (!siteId || !site) return
  afterOldScripts(() => {
    document.querySelectorAll<HTMLFormElement>(site.form).forEach((form) => bindSearchReplace(form, site, siteId))
  })
}
